// @ts-strict-ignore
import {Component, computed, inject, Injector, OnInit, viewChild} from "@angular/core";
import {Translations} from "@translations";
import {ParticipantType, SessionState, TranslationKey} from "@models";
import {MatStepper} from "@angular/material/stepper";
import {SimSessionService, TrackByMethods} from "@services";
import {ExamSessionStateService} from "./service/exam-session-state.service";
import {BasicMaterialDialog} from "../../basic-material-dialog/basic-material-dialog.directive";
import {AbstractControl} from "@angular/forms";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {SessionDialogInput, SessionDialogOutput} from "@common/models/session-dialog";

@UntilDestroy()
@Component({
	selector: "sim-exam-session-dialog",
	templateUrl: "./exam-session-dialog.component.html",
	styleUrls: ["./exam-session-dialog.component.scss"],
	providers: [ExamSessionStateService],
	standalone: false,
})
export class ExamSessionDialogComponent
	extends BasicMaterialDialog<ExamSessionDialogComponent, SessionDialogInput, SessionDialogOutput>
	implements OnInit
{
	// Readonly
	public readonly T = Translations;
	protected readonly ParticipantType = ParticipantType;
	protected readonly SessionState = SessionState;
	protected readonly TrackByMethods = TrackByMethods;

	// Injections
	public stateService: ExamSessionStateService = inject(ExamSessionStateService);
	public _sessionService: SimSessionService = inject(SimSessionService);

	// Signals
	public stepper = viewChild.required<MatStepper>("sessionStepper");
	public isLoading = this.stateService.isLoading;
	public currentStepForm = computed<AbstractControl>(() => this._computeCurrentStepForm());

	// Properties
	public session = this.stateService.session;
	public saveWarning: TranslationKey | null = null;

	constructor(injector: Injector) {
		super(injector);
	}

	public ngOnInit(): void {
		this.stateService.initState(this.data.sessionId, this.data.eventId, this.data.eventDay).then(() => this._checkShowWarning());
	}

	public async closeDialog(): Promise<void> {
		if (this.stateService.session.id) {
			const outputSession = await this._sessionService.getSessionById(this.stateService.session.id);
			this.dialogRef.close({action: "ok", outputSession});
		} else {
			this.dialogRef.close({action: "cancel", outputSession: null});
		}
	}

	private _computeCurrentStepForm(): AbstractControl {
		switch (this.stepper().selectedIndex) {
			case 0:
				return this.stateService.step1Form;
			case 1:
				return this.stateService.step2Form;
			case 2:
				return this.stateService.step3Form;
		}
	}

	private _checkShowWarning(): void {
		const doWarningCheck = () => {
			this.saveWarning = null;
			const step1 = this.stateService.step1Form;
			const step2 = this.stateService.step2Form;

			// Invalid Station
			if (step1?.valid && step2?.invalid) {
				this.saveWarning = Translations.EVENT_SESSION_EXAM_SESSION_WARNING_STATION_INVALID as TranslationKey;
				return;
			}
		};

		// OnChange
		this.stateService.$warningCheck.pipe(untilDestroyed(this)).subscribe(() => doWarningCheck());

		// OnLoad
		doWarningCheck();
	}
}
